import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { rupiah, num } from '../lib/format';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const PERIODS = [['bulan_ini', 'Bulan Ini'], ['30hari', '30 Hari'], ['semua', 'Semua Waktu']];

export default function Laporan() {
  const [period, setPeriod] = useState('bulan_ini');
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get('/report/profit', { params: { period } }).then((r) => setRows(r.data));
  }, [period]);

  const total = rows.reduce((a, r) => ({
    omzet: a.omzet + r.omzet, hpp: a.hpp + r.hpp_total, pot: a.pot + r.potongan_total, laba: a.laba + r.laba_total,
  }), { omzet: 0, hpp: 0, pot: 0, laba: 0 });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {PERIODS.map(([k, l]) => (
          <button key={k} onClick={() => setPeriod(k)} data-testid={`period-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${period === k ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-secondary'}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Omzet" value={rupiah(total.omzet)} testid="rep-omzet" />
        <Stat label="Total HPP" value={rupiah(total.hpp)} testid="rep-hpp" />
        <Stat label="Potongan" value={rupiah(total.pot)} testid="rep-potongan" />
        <Stat label="Laba Bersih" value={rupiah(total.laba)} testid="rep-laba" accent />
      </div>

      <div className="bg-card border border-border/60 rounded-xl p-5 lg:p-6">
        <h3 className="font-display text-lg font-semibold mb-4">Kontribusi Laba per Produk</h3>
        {rows.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">Belum ada penjualan pada periode ini.</div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="product_nama" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `${num(v / 1000)}rb`} />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} formatter={(v) => rupiah(v)} />
                <Bar dataKey="omzet" fill="hsl(var(--chart-2))" radius={[6, 6, 0, 0]} name="Omzet" maxBarSize={60} />
                <Bar dataKey="laba_total" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} name="Laba" maxBarSize={60} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="bg-card border border-border/60 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="table-report">
            <thead className="bg-secondary/50 text-left">
              <tr>
                <th className="p-3 font-semibold">Produk</th>
                <th className="p-3 font-semibold text-right">Terjual</th>
                <th className="p-3 font-semibold text-right">Omzet</th>
                <th className="p-3 font-semibold text-right">HPP</th>
                <th className="p-3 font-semibold text-right">Potongan</th>
                <th className="p-3 font-semibold text-right">Laba Bersih</th>
                <th className="p-3 font-semibold text-right">Margin</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-muted-foreground">Belum ada penjualan pada periode ini.</td></tr>}
              {rows.map((r) => (
                <tr key={r.product_id} className="border-t border-border/60">
                  <td className="p-3 font-medium">{r.product_nama}</td>
                  <td className="p-3 text-right tabular-nums">{num(r.qty_terjual, 2)}</td>
                  <td className="p-3 text-right tabular-nums">{rupiah(r.omzet)}</td>
                  <td className="p-3 text-right tabular-nums">{rupiah(r.hpp_total)}</td>
                  <td className="p-3 text-right tabular-nums">{rupiah(r.potongan_total)}</td>
                  <td className="p-3 text-right tabular-nums font-semibold text-emerald-600 dark:text-emerald-400">{rupiah(r.laba_total)}</td>
                  <td className="p-3 text-right tabular-nums">{num(r.margin, 1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className="stat-card" data-testid={testid}>
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className={`mt-2 font-display text-xl sm:text-2xl font-semibold ${accent ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>{value}</div>
    </div>
  );
}
